import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError, type QuickReplyView, type TeamMemberView } from '../lib/api'
import { Banner, Button, Card, TextField } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const VISIBILITY_LABEL: Record<QuickReplyView['visibility'], string> = {
  all: 'Everyone',
  members: 'Specific people',
  roles: 'Specific roles',
}

const ROLES: { id: TeamMemberView['role']; label: string }[] = [
  { id: 'owner', label: 'Owner' },
  { id: 'admin', label: 'Admin' },
  { id: 'agent', label: 'Agent' },
  { id: 'viewer', label: 'Viewer' },
]

/**
 * The all/members/roles picker shared by the create form and the edit row — visibility is the
 * one field both need identically, so a rule change here can't drift between the two forms.
 */
function VisibilityFields({
  visibility,
  visibilityIds,
  members,
  onChange,
}: {
  visibility: 'all' | 'members' | 'roles'
  visibilityIds: string[]
  members: TeamMemberView[]
  onChange: (visibility: 'all' | 'members' | 'roles', visibilityIds: string[]) => void
}) {
  const options = visibility === 'roles' ? ROLES.map((r) => ({ id: r.id, label: r.label })) : members.map((m) => ({ id: m.userId, label: m.name }))

  return (
    <div className="cad-field">
      <label>Visible to</label>
      <div className="row gap-3 wrap">
        {(['all', 'members', 'roles'] as const).map((option) => (
          <label key={option} className="row gap-1" style={{ alignItems: 'center' }}>
            <input
              type="radio"
              checked={visibility === option}
              onChange={() => onChange(option, option === 'all' ? [] : visibilityIds)}
            />
            <span>{VISIBILITY_LABEL[option]}</span>
          </label>
        ))}
      </div>
      {visibility !== 'all' && (
        <div className="row gap-2 wrap" style={{ marginTop: '6px' }}>
          {options.length === 0 ? (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Nobody to choose from yet.</span>
          ) : (
            options.map((option) => (
              <label key={option.id} className="cad-pill" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={visibilityIds.includes(option.id)}
                  onChange={(e) =>
                    onChange(
                      visibility,
                      e.target.checked ? [...visibilityIds, option.id] : visibilityIds.filter((id) => id !== option.id),
                    )
                  }
                  style={{ marginRight: '4px' }}
                />
                {option.label}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Where a workspace writes its own `/shortcut` phrasings. Before this screen existed a quick
 * reply could only be edited, never created — the whole set came from a one-time seed at
 * provisioning. This is the first create/delete surface they've had.
 */
export function QuickReplySettings() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [shortcut, setShortcut] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<'all' | 'members' | 'roles'>('all')
  const [visibilityIds, setVisibilityIds] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)

  const replies = useQuery({ queryKey: ['quick-replies'], queryFn: api.quickReplies, retry: false })
  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['quick-replies'] })

  const create = useMutation({
    mutationFn: () =>
      api.createQuickReply({ shortcut: shortcut.trim(), title: title.trim(), body: body.trim(), visibility, visibilityIds }),
    onSuccess: async () => {
      setShortcut('')
      setTitle('')
      setBody('')
      setVisibility('all')
      setVisibilityIds([])
      setCreating(false)
      await refresh()
    },
  })

  const remove = useMutation({ mutationFn: api.deleteQuickReply, onSuccess: refresh })
  // A separate mutation from `remove`: sharing one would mean a failed save in the still-open
  // edit row shows up mixed in with delete errors from an entirely different row, the same
  // cross-item leakage this codebase has already fixed elsewhere (Team.tsx, Knowledge.tsx).
  const edit = useMutation({
    mutationFn: (input: {
      id: string
      title: string
      body: string
      visibility: 'all' | 'members' | 'roles'
      visibilityIds: string[]
    }) =>
      api.updateQuickReply(input.id, {
        title: input.title,
        body: input.body,
        visibility: input.visibility,
        visibilityIds: input.visibilityIds,
      }),
    onSuccess: () => {
      setEditingId(null)
      void refresh()
    },
  })

  if (replies.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (replies.isError) {
    const message = replies.error instanceof ApiError ? replies.error.message : 'Quick replies unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const list = replies.data!.replies
  const canManage = replies.data!.canManage
  const createError = create.error instanceof ApiError ? create.error.message : undefined

  return (
    <>
      {!canManage && <ReadOnlyNotice what="quick replies" />}
      {/* The active tab above already says "Quick replies". */}
      <p className="cad-muted" style={{ maxWidth: '62ch' }}>
        Saved phrasings an agent triggers by typing <code>/shortcut</code> in the reply box —
        faster than retyping the same answer every time it comes up.
      </p>

      {canManage && (
        <div className="row">
          {!creating ? (
            <Button variant="tonal" onClick={() => setCreating(true)}>
              Create quick reply
            </Button>
          ) : (
            <Card variant="outlined">
              <form
                className="stack gap-3"
                onSubmit={(event: FormEvent) => {
                  event.preventDefault()
                  if (shortcut.trim() && title.trim() && body.trim()) create.mutate()
                }}
              >
                <TextField
                  label="Shortcut"
                  value={shortcut}
                  onChange={(e) => setShortcut(e.target.value)}
                  placeholder="delivery-eta"
                />
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)', margin: 0 }}>
                  Typed as <code>/{shortcut.trim().replace(/^\/+/, '').replace(/\s+/g, '-').toLowerCase() || 'shortcut'}</code>
                </p>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Delivery ETA"
                />
                <div className="cad-field">
                  <label htmlFor="qr-body">Message</label>
                  <textarea
                    id="qr-body"
                    className="cad-input"
                    rows={3}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Standard delivery takes three to five working days."
                  />
                </div>
                <VisibilityFields
                  visibility={visibility}
                  visibilityIds={visibilityIds}
                  members={team.data?.members ?? []}
                  onChange={(next, nextIds) => {
                    setVisibility(next)
                    setVisibilityIds(nextIds)
                  }}
                />
                {createError && <Banner tone="error">{createError}</Banner>}
                <div className="row gap-2">
                  <Button
                    type="submit"
                    disabled={!shortcut.trim() || !title.trim() || !body.trim() || create.isPending}
                  >
                    {create.isPending ? 'Creating…' : 'Create'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setCreating(false)
                      setShortcut('')
                      setTitle('')
                      setBody('')
                      setVisibility('all')
                      setVisibilityIds([])
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}
        </div>
      )}

      <section className="cad-card">
        {remove.error && (
          <Banner tone="error">
            {remove.error instanceof ApiError || remove.error instanceof Error
              ? remove.error.message
              : 'Could not delete that.'}
          </Banner>
        )}
        {list.length === 0 ? (
          <p className="cad-muted">No quick replies yet. Create the first one above.</p>
        ) : (
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  <th>Shortcut</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Visible to</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((reply) =>
                  editingId === reply.id ? (
                    <EditQuickReplyRow
                      key={reply.id}
                      reply={reply}
                      members={team.data?.members ?? []}
                      colSpan={canManage ? 5 : 4}
                      saving={edit.isPending}
                      error={edit.error}
                      onSave={(fields) => edit.mutate({ id: reply.id, ...fields })}
                      onCancel={() => {
                        edit.reset()
                        setEditingId(null)
                      }}
                    />
                  ) : (
                    <tr key={reply.id}>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{reply.shortcut}</td>
                      <td>{reply.title}</td>
                      <td
                        className="cad-muted"
                        style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {reply.body}
                      </td>
                      <td>
                        <span className="cad-pill">{VISIBILITY_LABEL[reply.visibility]}</span>
                      </td>
                      {canManage && (
                        <td>
                          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              onClick={() => {
                                edit.reset()
                                setEditingId(reply.id)
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              disabled={remove.isPending}
                              onClick={() => {
                                if (confirm(`Delete the "/${reply.shortcut}" quick reply?\n\nAnyone who types it will just get plain text. This cannot be undone.`)) remove.mutate(reply.id)
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

/** Inline edit for one existing quick reply — title, message, and visibility. The shortcut
 *  stays fixed: it's what the composer's `/` autocomplete matches on, and the backend's update
 *  route has never accepted changing it, the same as when it was create-only. */
function EditQuickReplyRow({
  reply,
  members,
  colSpan,
  saving,
  error,
  onSave,
  onCancel,
}: {
  reply: QuickReplyView
  members: TeamMemberView[]
  colSpan: number
  saving: boolean
  error: unknown
  onSave: (fields: { title: string; body: string; visibility: 'all' | 'members' | 'roles'; visibilityIds: string[] }) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(reply.title)
  const [body, setBody] = useState(reply.body)
  const [visibility, setVisibility] = useState(reply.visibility)
  const [visibilityIds, setVisibilityIds] = useState(reply.visibilityIds)
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="stack gap-3" style={{ padding: '8px 0' }}>
          <div className="cad-field">
            <label htmlFor={`qr-edit-title-${reply.id}`}>Title</label>
            <input
              id={`qr-edit-title-${reply.id}`}
              className="cad-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="cad-field">
            <label htmlFor={`qr-edit-body-${reply.id}`}>Message</label>
            <textarea
              id={`qr-edit-body-${reply.id}`}
              className="cad-input"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <VisibilityFields
            visibility={visibility}
            visibilityIds={visibilityIds}
            members={members}
            onChange={(next, nextIds) => {
              setVisibility(next)
              setVisibilityIds(nextIds)
            }}
          />
          {message && <Banner tone="error">{message}</Banner>}
          <div className="row gap-2">
            <button
              type="button"
              className="cad-btn"
              disabled={!title.trim() || !body.trim() || saving}
              onClick={() => onSave({ title: title.trim(), body: body.trim(), visibility, visibilityIds })}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  )
}
